<script setup lang="ts">
import { defineComponent } from 'vue'
import { definePage } from 'vue-router/auto'
import { Icon } from '@iconify/vue'
import { Card } from '~/components/ui/card'
import { useResetPasswordService } from '~/services/auth/useResetPasswordService'
import Button from '~/components/ui/button/Button.vue'
import { buttonVariants } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import Input from '~/components/ui/input/Input.vue'
import Label from '~/components/ui/label/Label.vue'
import PasswordChecklist from '~/components/auth/PasswordChecklist.vue'

definePage({
  meta: {
    requiresAuth: false,
    allowAuthenticated: true,
  },
})

defineComponent({
  name: 'ResetPassword',
})

const { password, password_confirmation, errors, onSubmit, isLoading, changed, expired } =
  useResetPasswordService()

// Moves focus to a screen's heading when the step changes, so screen readers announce it.
const vFocus = { mounted: (el: HTMLElement) => el.focus() }
</script>

<template>
  <Card class="w-full max-w-[400px] px-6 pb-6 pt-7 shadow-sm">
    <div v-if="expired" class="flex flex-col gap-6">
      <span
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary"
      >
        <Icon icon="lucide:clock" class="h-5 w-5" aria-hidden="true" />
      </span>
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
          {{ $t('Professional portal') }}
        </span>
        <h1
          v-focus
          tabindex="-1"
          class="font-display text-2xl font-semibold leading-[1.2] tracking-[-0.01em] outline-none"
        >
          {{ $t('This link has expired or was already used') }}
        </h1>
        <p class="text-sm text-muted-foreground">
          {{ $t('Ask for a new link and we will email it to you.') }}
        </p>
      </div>
      <div class="flex flex-col gap-3">
        <router-link
          to="/auth/forgot-password"
          :class="
            cn(buttonVariants({ variant: 'default' }), 'h-10 w-full font-semibold max-sm:h-12')
          "
        >
          {{ $t('Send me a new link') }}
        </router-link>
        <router-link to="/auth/login" class="text-center text-sm underline hover:text-primary">
          {{ $t('Back to sign in') }}
        </router-link>
      </div>
    </div>

    <div v-else-if="changed" class="flex flex-col gap-6">
      <span
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary"
      >
        <Icon icon="lucide:check" class="h-5 w-5" aria-hidden="true" />
      </span>
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
          {{ $t('Professional portal') }}
        </span>
        <h1
          v-focus
          tabindex="-1"
          class="font-display text-2xl font-semibold leading-[1.2] tracking-[-0.01em] outline-none"
        >
          {{ $t('Your password is set') }}
        </h1>
        <p class="text-sm text-muted-foreground">
          {{ $t('You can now sign in with your new password.') }}
        </p>
      </div>
      <router-link
        to="/auth/login"
        :class="cn(buttonVariants({ variant: 'default' }), 'h-10 w-full font-semibold max-sm:h-12')"
      >
        {{ $t('Go to sign in') }}
      </router-link>
    </div>

    <form v-else class="flex flex-col gap-6" novalidate @submit="onSubmit">
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
          {{ $t('Professional portal') }}
        </span>
        <h1 class="font-display text-2xl font-semibold leading-[1.2] tracking-[-0.01em]">
          {{ $t('Choose your password') }}
        </h1>
      </div>
      <div class="flex flex-col gap-4">
        <div class="grid gap-2">
          <Label for="password">{{ $t('New password') }}</Label>
          <Input
            id="password"
            v-model="password"
            :error-message="errors.password"
            type="password"
            autocomplete="new-password"
            name="password"
            autofocus
            :aria-invalid="!!errors.password"
            aria-describedby="password-requirements"
            class="h-10 max-sm:h-11 max-sm:text-base"
          />
          <PasswordChecklist id="password-requirements" :password="password ?? ''" />
        </div>
        <div class="grid gap-2">
          <Label for="password_confirmation">{{ $t('Repeat password') }}</Label>
          <Input
            id="password_confirmation"
            v-model="password_confirmation"
            :error-message="errors.password_confirmation"
            type="password"
            autocomplete="new-password"
            name="password_confirmation"
            :aria-invalid="!!errors.password_confirmation"
            class="h-10 max-sm:h-11 max-sm:text-base"
          />
        </div>
      </div>
      <Button type="submit" class="h-10 w-full font-semibold max-sm:h-12" :is-loading="isLoading">
        {{ $t('Save password') }}
      </Button>
    </form>
  </Card>
</template>
